import {
  Component,
  Input,
  TemplateRef,
  ViewContainerRef,
  ElementRef,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateService } from '@ngx-translate/core';
import {
  Overlay,
  OverlayRef,
  OverlayPositionBuilder,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  TooltipDialogComponent,
  TooltipDialogData,
} from '../tooltip-dialog/tooltip-dialog.component';

@Component({
  selector: 'app-tooltip-button',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule],
  templateUrl: './tooltip-button.component.html',
  styleUrl: './tooltip-button.component.scss',
})
export class TooltipButtonComponent implements OnInit, OnDestroy {
  @Input() tooltipKey!: string;
  @Input() title?: string;
  @Input() content?: string;
  @Input() template?: TemplateRef<any>;
  @Input() data?: any;
  @Input() position: 'inline' | 'float' | 'after' = 'inline';

  isButtonVisible = true;
  isInRequiredField = false;
  private overlayRef?: OverlayRef;
  private isTooltipVisible = false;

  constructor(
    private overlay: Overlay,
    private overlayPositionBuilder: OverlayPositionBuilder,
    private elementRef: ElementRef,
    private viewContainerRef: ViewContainerRef,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.checkTooltipAvailability();
    this.checkIfInRequiredField();

    // Also check after a short delay to ensure Angular Material has rendered
    setTimeout(() => {
      this.checkIfInRequiredField();
    }, 100);
  }

  private checkIfInRequiredField(): void {
    // Reset the flag
    this.isInRequiredField = false;

    // Check if we're inside a required mat-form-field
    let element = this.elementRef.nativeElement.parentElement;
    while (element) {
      // First check if this is a mat-form-field
      if (
        element.classList.contains('mat-mdc-form-field') ||
        element.tagName === 'MAT-FORM-FIELD'
      ) {
        // Now check if this specific form field has required inputs
        const hasRequiredInput =
          element.querySelector('input[required]') ||
          element.querySelector('textarea[required]') ||
          element.querySelector('select[required]');
        if (hasRequiredInput) {
          this.isInRequiredField = true;
        }
        break; // Stop at the form field level
      }

      // Also check for required label classes
      if (
        element.classList.contains('mdc-floating-label--required') ||
        element.classList.contains('mat-form-field-required')
      ) {
        this.isInRequiredField = true;
        break;
      }

      element = element.parentElement;
      // Stop if we've gone too far up the DOM tree
      if (!element || element.tagName === 'BODY') {
        break;
      }
    }
  }

  private isInFloatingLabelContext(): boolean {
    // Check if we're inside a floating label
    let element = this.elementRef.nativeElement.parentElement;
    while (element) {
      if (
        element.classList.contains('mdc-floating-label--float-above') ||
        element.classList.contains('mat-mdc-floating-label')
      ) {
        return true;
      }
      element = element.parentElement;
      if (!element || element.tagName === 'BODY') {
        break;
      }
    }
    return false;
  }

  private checkTooltipAvailability(): void {
    // If content is provided directly, always show button
    if (this.content) {
      this.isButtonVisible = true;
      return;
    }

    // Check if tooltip translations exist
    if (this.tooltipKey) {
      this.translateService
        .get([
          `${this.tooltipKey}.tooltipTitle`,
          `${this.tooltipKey}.tooltipDescription`,
        ])
        .subscribe(translations => {
          const tooltipTitle = translations[`${this.tooltipKey}.tooltipTitle`];
          const tooltipDescription =
            translations[`${this.tooltipKey}.tooltipDescription`];

          // Check if we have valid translations (not just the key returned)
          const hasTitle =
            tooltipTitle && tooltipTitle !== `${this.tooltipKey}.tooltipTitle`;
          const hasDescription =
            tooltipDescription &&
            tooltipDescription !== `${this.tooltipKey}.tooltipDescription`;

          // Only show button if we have valid translations
          this.isButtonVisible = hasTitle || hasDescription;
        });
    } else {
      // No tooltip key provided, hide button
      this.isButtonVisible = false;
    }
  }

  openTooltip(event?: Event): void {
    // Prevent the click from propagating to parent elements
    if (event) {
      event.stopPropagation();
    }

    if (this.isTooltipVisible) {
      this.closeTooltip();
      return;
    }

    // Get tooltip content
    let tooltipData: TooltipDialogData;

    if (this.content) {
      // Use provided content
      tooltipData = {
        title: this.title,
        content: this.content,
        template: this.template,
        data: this.data,
        isFloatingLabelContext: this.isInFloatingLabelContext(),
      };
      this.showTooltipOverlay(tooltipData);
    } else if (this.tooltipKey) {
      // Get tooltip data from translation service using the new nested structure
      this.translateService
        .get([
          `${this.tooltipKey}.tooltipTitle`,
          `${this.tooltipKey}.tooltipDescription`,
        ])
        .subscribe(translations => {
          const tooltipTitle = translations[`${this.tooltipKey}.tooltipTitle`];
          const tooltipDescription =
            translations[`${this.tooltipKey}.tooltipDescription`];

          // Check if we have valid translations (not just the key returned)
          const hasTitle =
            tooltipTitle && tooltipTitle !== `${this.tooltipKey}.tooltipTitle`;
          const hasDescription =
            tooltipDescription &&
            tooltipDescription !== `${this.tooltipKey}.tooltipDescription`;

          // Since button is only visible when translations exist, we can safely use them
          tooltipData = {
            title: this.title || (hasTitle ? tooltipTitle : undefined),
            content: hasDescription
              ? tooltipDescription
              : hasTitle
                ? tooltipTitle
                : 'No tooltip content available.',
            template: this.template,
            data: this.data,
            isFloatingLabelContext: this.isInFloatingLabelContext(),
          };
          this.showTooltipOverlay(tooltipData);
        });
    }
  }

  private showTooltipOverlay(tooltipData: TooltipDialogData): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
    }

    // Create overlay position strategy
    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions([
        {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom',
          offsetY: -8,
        },
        {
          originX: 'center',
          originY: 'bottom',
          overlayX: 'center',
          overlayY: 'top',
          offsetY: 8,
        },
        {
          originX: 'end',
          originY: 'center',
          overlayX: 'start',
          overlayY: 'center',
          offsetX: 8,
        },
        {
          originX: 'start',
          originY: 'center',
          overlayX: 'end',
          overlayY: 'center',
          offsetX: -8,
        },
      ])
      .withPush(true)
      .withViewportMargin(16);

    // Create overlay
    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: 'tooltip-overlay-backdrop',
    });

    // Create and attach component
    const tooltipPortal = new ComponentPortal(
      TooltipDialogComponent,
      this.viewContainerRef,
    );
    const componentRef = this.overlayRef.attach(tooltipPortal);

    // Detect the actual position used by the overlay
    positionStrategy.positionChanges.subscribe(position => {
      let arrowPosition: 'top' | 'bottom' | 'left' | 'right' = 'top';

      if (position.connectionPair.originY === 'top') {
        // Tooltip is above the button, arrow should be at bottom pointing down
        arrowPosition = 'bottom';
      } else if (position.connectionPair.originY === 'bottom') {
        // Tooltip is below the button, arrow should be at top pointing up
        arrowPosition = 'top';
      } else if (position.connectionPair.originX === 'end') {
        // Tooltip is to the right of the button, arrow should be at left pointing left
        arrowPosition = 'left';
      } else if (position.connectionPair.originX === 'start') {
        // Tooltip is to the left of the button, arrow should be at right pointing right
        arrowPosition = 'right';
      }

      componentRef.instance.data = { ...tooltipData, arrowPosition };
      componentRef.instance.data.arrowPosition = arrowPosition;
      componentRef.changeDetectorRef.detectChanges();
    });

    // Pass data to component with initial arrow position
    componentRef.instance.data = {
      ...tooltipData,
      arrowPosition: 'top',
      isFloatingLabelContext: this.isInFloatingLabelContext(),
    };
    componentRef.instance.isOverlay = true;
    componentRef.instance.closeTooltip = () => this.closeTooltip();

    // Close on backdrop click
    this.overlayRef.backdropClick().subscribe(() => this.closeTooltip());

    // Close on escape key
    this.overlayRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.closeTooltip();
      }
    });

    this.isTooltipVisible = true;
  }

  private closeTooltip(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = undefined;
    }
    this.isTooltipVisible = false;
  }

  ngOnDestroy(): void {
    this.closeTooltip();
  }
}
